import { useEffect, useState } from 'react';
import Button from 'components/control/button';
import Link from 'components/link';
import styles from './styles.module.scss';
import Image from 'next/image';
import SimilarListings, {
  SimilarListingsComponentUsage,
} from 'components/dynamic-page/listing-page/similar-listings-sections/similar-listings';
import { SimilarListingCardData } from 'components/dynamic-page/listing-page/similar-listings-sections/similar_listing_card_data';
import Listing, { getListingsBySlug, getSimilarListings, getSimilarListingsByLocation, GetSimilarListingsByLocationProps } from 'data/listing';
import { SimilarListing } from '@zoocasa/go-search';
import { similarListingToSimilarListingCardData } from 'components/dynamic-page/listing-page/similar-listings-sections';
import { useModalContext, useThemeContext } from 'contexts';
import { PagesList } from 'types/pages';
import { dasherizeKeys } from '@zoocasa/node-kit/objects/transform-keys';
import getZoopraisal, { Zoopraisal } from 'utils/zoopraisal';
import { getSlugFromAddress, parseGooglePlaceApiObject, ParsedAddress } from 'utils/google-maps/zoopraisal';
import HomeAppraisalConnectionRequest from 'components/home-appraisal-connection-request';
import { useIsNotMobile } from 'hooks/use-size-class';
import formatCurrency from 'utils/format-currency';
import EditIcon from 'components/icon/edit-icon';
import HomeAppraisalStorage from 'utils/appraisal-storage';
import { ProvinceOrStateCode, provinceOrStateNameFromCode } from 'utils/province_or_state';
import BedIcon from 'components/icon/bed-icon';
import BathIcon from 'components/icon/bath-icon';
import SqftIcon from 'components/icon/sqft-icon';
import CarIcon from 'components/icon/car-icon';
import SqftText from 'components/sqft-text';
import { capitalize, dasherize } from '@zoocasa/node-kit/strings';
import HomeTypeIcon from 'components/icon/home-type-icon';
import LockerIcon from 'components/icon/locker-icon';
import PriceHistory from 'components/price-history';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_APPRAISAL_DETAIL_ESTIMATE_FOR_FREE, GTM_CLICK_APPRAISAL_EDIT_PROPERTY_DETAILS,
  GTM_CLICK_APPRAISAL_NO_ESTIMATE_BACK_TO_SEARCH, GTM_CLICK_APPRAISAL_SPEAK_TO_AGENT, GTM_CLICK_APPRAISAL_TALK_TO_AGENT,
  GTM_VIEW_APPRAISAL_ESTIMATE, GTM_VIEW_APPRAISAL_ESTIMATE_EXPANDED, GTM_VIEW_APPRAISAL_ESTIMATE_FOR_SALE_RANGE, GTM_VIEW_APPRAISAL_NO_ESTIMATE } from 'constants/events';
import LoadingSpinner from 'components/loading-spinner';
import { AVAILABLE_STATUS, NOT_AVAILABLE_SOLD_STATUS } from 'contexts/preferences/listing-params';
import generateNearbyListingsDescription from 'utils/generate-nearby-listings-description';
import { ThemeNames } from 'types/themes';
import { buildClassName } from 'utils/build-class-name';
import InfoIcon from 'components/icon/info-icon';
import ValuationTips from '../valuation-tips';

type SimilarListingState = {
  similarListings: SimilarListingCardData[] | null[];
  isLoading: boolean;
};

const LOWER_MULTIPLIER = 0.95; // 95 percent
const UPPER_MULTIPLIER = 1.1; // 110 percent
const DEFAULT_ESTIMATE = 0;
export const STATUS_ID_AVAILABLE = 1;
export const STATUS_ID_NOT_AVAILABLE_SOLD = 3;


export default function HomeAppraisalResults() {
  const propertyDetailsStored = HomeAppraisalStorage.getDetails() as any;
  const propertyStored = HomeAppraisalStorage.getProperty() as any;

  let parsedAddress: ParsedAddress | null = null;
  if (propertyStored && propertyStored.address_components) {
    parsedAddress = parseGooglePlaceApiObject(propertyStored);
  }

  let listing: Listing | any;

  if (parsedAddress) {
    const slug = getSlugFromAddress(parsedAddress);
    listing = new Listing({ id: null, attributes: {
      'street-name': propertyDetailsStored?.address || propertyStored.streetAddress,
      'unit-number': propertyDetailsStored?.unitNumber || propertyStored.unitNumber,
      bedrooms: parseInt(propertyDetailsStored?.bedrooms) || propertyStored.bedrooms,
      bathrooms: parseInt(propertyDetailsStored?.bathrooms) || propertyStored.bathrooms,
      'style-name': dasherize(propertyDetailsStored?.homeType) || propertyStored.homeType,
      parking: propertyDetailsStored?.parking || propertyStored.parking,
      'square-footage': propertyDetailsStored?.sqft ? { min: propertyDetailsStored?.sqft } : propertyStored.squareFootage,
      locker: propertyDetailsStored?.locker || propertyStored.locker,
      addressSlug: slug,
      neighbourhoodName: parsedAddress.neighborhood || parsedAddress.sublocality_level_1 || '',
      city: parsedAddress.locality || parsedAddress.administrative_area_level_1,
      position: parsedAddress.position,
    }});
  } else if (propertyStored) {
    listing = new Listing({ id: propertyStored.id, attributes: {
      ...dasherizeKeys(propertyStored as unknown as Record<string, unknown>),
      'street-name': propertyDetailsStored?.address || propertyStored.streetAddress,
      'unit-number': propertyDetailsStored?.unitNumber || propertyStored.unitNumber,
      bedrooms: parseInt(propertyDetailsStored?.bedrooms) || propertyStored.bedrooms,
      bathrooms: parseInt(propertyDetailsStored?.bathrooms) || propertyStored.bathrooms,
      'style-name': (propertyDetailsStored?.homeType || propertyStored.homeType) && dasherize(propertyDetailsStored?.homeType) || propertyStored.homeType,
      parking: propertyDetailsStored?.parking || propertyStored.parking,
      'square-footage': propertyDetailsStored?.sqft ? { min: propertyDetailsStored?.sqft } : propertyStored.squareFootage,
      locker: propertyDetailsStored?.locker || propertyStored.locker,
      neighbourhoodName: propertyStored.neighbourhoodName || '',
      city: propertyDetailsStored?.city || propertyStored?.city,
      position: propertyDetailsStored?.position || propertyStored?.position,
    }});
  } else listing = undefined;

  const [similarAvailableListingState, setSimilarAvailableListingState] = useState<SimilarListingState>({
    similarListings: [],
    isLoading: true,
  });

  const [similarSoldListingState, setSimilarSoldListingState] = useState<SimilarListingState>({
    similarListings: [],
    isLoading: true,
  });

  const getSimilarListingsHandler = async (status: string, size = 10, minPrice?: number | undefined, maxPrice?: number | undefined) => {
    if (listing.id) {
      return getSimilarListings(listing.id, size, status, minPrice, maxPrice);
    }
    const props: GetSimilarListingsByLocationProps = {
      position: listing.position,
      size,
      homeType: listing['style-name'],
      bedrooms: listing.bedrooms,
      bathrooms: listing.bathrooms,
      sqft: propertyDetailsStored.sqft ? propertyDetailsStored.sqft : null,
      status,
      minPrice,
      maxPrice,
    };

    return getSimilarListingsByLocation(props);
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExpandedEstimate, setIsExpandedEstimate] = useState<boolean>(false);
  const [is10kmEstimate, setIs10kmEstimate] = useState<boolean>(false);
  const [lowerEstimate, setLowerEstimate] = useState<number>(0);
  const [upperEstimate, setUpperEstimate] = useState<number>(0);
  const [zoopraisalData, setZoopraisalData] = useState<Zoopraisal | null>();
  const [listings, setListings] = useState<Listing[]>([]);
  const nonDeletedListings = listings.filter(listing => listing.status !== 'deleted');
  const { openModal } = useModalContext();
  const { themeName } = useThemeContext();
  const isExp = themeName && themeName !== ThemeNames.ZOOCASA;
  const isNotMobile = useIsNotMobile();
  const noEstimate = !propertyDetailsStored || !propertyStored || !listing || !zoopraisalData?.isAvailable;
  const emptyImage = isExp ? '/next/assets/images/home-appraisal/not-available-exp.svg' : '/next/assets/images/home-appraisal/not-available.svg';
  const notEnoughDataDisclaimer = is10kmEstimate ? 'We currently do not have enough sold properties in this area to provide a valuation. We can help with a free in person home estimate.'
    : 'There’s not quite enough online sold data in your immediate area to provide a single valuation. We recommend contacting one of our agents who have access to more offline data.';

  const fetchSimilarListings = async (price: number | null | undefined) => {
    if (similarSoldListingState.isLoading) {
      const similarSoldListings = await getSimilarListingsHandler(NOT_AVAILABLE_SOLD_STATUS, 6);
      const formattedSoldSimilarListings = similarSoldListings.map((listing: SimilarListing) => similarListingToSimilarListingCardData(listing));
      setSimilarSoldListingState({
        similarListings: formattedSoldSimilarListings,
        isLoading: false,
      });
    }

    if (similarAvailableListingState.isLoading) {
      const valuationPrice = price || 0;
      const similarAvailableListingsAbove = await getSimilarListingsHandler(AVAILABLE_STATUS, 8, valuationPrice);
      const formattedAvailableSimilarListingsAbove = similarAvailableListingsAbove
        .map((listing: SimilarListing) => similarListingToSimilarListingCardData(listing))
        .sort((a, b) => a.price - b.price); // Sort by price in ascending order

      let formattedAvailableSimilarListings: SimilarListingCardData[] = [];
      if (formattedAvailableSimilarListingsAbove.length >= 8) { // Case 1: 8 or more properties above valuation price
        formattedAvailableSimilarListings = formattedAvailableSimilarListingsAbove.slice(0, 8);
      } else if (formattedAvailableSimilarListingsAbove.length > 1) { // Case 2: Less than 8 but more than 1 property above valuation price
        const similarBelowSize = 8 - formattedAvailableSimilarListingsAbove.length;
        const similarAvailableListingsBelow = await getSimilarListingsHandler(AVAILABLE_STATUS, similarBelowSize, valuationPrice);
        const formattedAvailableSimilarListingsBelow = similarAvailableListingsBelow
          .map((listing: SimilarListing) => similarListingToSimilarListingCardData(listing))
          .sort((a, b) => b.price - a.price); // Sort by price in descending order

        formattedAvailableSimilarListings = [
          ...formattedAvailableSimilarListingsAbove,
          ...formattedAvailableSimilarListingsBelow.slice(0, 8 - formattedAvailableSimilarListingsAbove.length),
        ];
      } else { // Case 3: No properties above valuation price
        const similarAvailableListingsBelow = await getSimilarListingsHandler(AVAILABLE_STATUS, 8, 0, valuationPrice);
        const formattedAvailableSimilarListingsBelow = similarAvailableListingsBelow
          .map((listing: SimilarListing) => similarListingToSimilarListingCardData(listing))
          .sort((a, b) => b.price - a.price); // Sort by price in descending order

        if (formattedAvailableSimilarListingsBelow.length >= 4) {
          formattedAvailableSimilarListings = formattedAvailableSimilarListingsBelow.slice(0, 8);
        }
      }

      setSimilarAvailableListingState({
        similarListings: formattedAvailableSimilarListings,
        isLoading: false,
      });
    }
  };

  const dynamicAvailableListingsTitle = generateNearbyListingsDescription(listing);

  const speakToAnAgent = () => {
    trackEvent(GTM_CLICK_APPRAISAL_SPEAK_TO_AGENT);
    if (!listing?.id) {
      openModal('connect-with-agent', { source: 'CMA', defaultOption: 'buy', page: PagesList.HOME_APPRAISAL });
    } else {
      openModal('home-appraisal-connection-request-popup', { listing, noEstimate: true });
    }
  };

  const getEstimate = () => {
    trackEvent(GTM_CLICK_APPRAISAL_TALK_TO_AGENT);
    if (isNotMobile) {
      openModal('home-appraisal-connection-request-popup', { listing: listing! });
    } else {
      openModal('home-appraisal-connection-request', { listing: listing! });
    }
  };
  const getDetailedEstimate = () => {
    trackEvent(GTM_CLICK_APPRAISAL_DETAIL_ESTIMATE_FOR_FREE);
    openModal('home-appraisal-connection-request', { listing: listing! });
  };

  const fetchListings = async () => setListings((await getListingsBySlug({ slug: listing!.addressSlug!, serializer: '' })).listings);

  useEffect(() => {
    if (listing && listing.addressSlug) fetchListings();

    if (!zoopraisalData) {
      getZoopraisal({ listing }).then(data => {
        if (!data?.isAvailable) {
          getZoopraisal({ listing, monthsAgo: 12, distance: 2000 }).then(secondEstimate => {
            setZoopraisalData(secondEstimate);
            setIsExpandedEstimate(true);
            setLowerEstimate(secondEstimate?.averagePrice * LOWER_MULTIPLIER || DEFAULT_ESTIMATE);
            setUpperEstimate(secondEstimate?.averagePrice * UPPER_MULTIPLIER || DEFAULT_ESTIMATE);
            if (secondEstimate?.isAvailable) {
              trackEvent(GTM_VIEW_APPRAISAL_ESTIMATE);
              trackEvent(GTM_VIEW_APPRAISAL_ESTIMATE_EXPANDED);
            } else {

              getZoopraisal({ listing, monthsAgo: 12, distance: 10000, statusId: STATUS_ID_AVAILABLE }).then(thirdEstimate => {
                setZoopraisalData(thirdEstimate);
                if (thirdEstimate?.isAvailable && thirdEstimate?.datasetCount >= 1) {
                  setIs10kmEstimate(true);
                  setLowerEstimate(thirdEstimate?.lowestPrice);
                  setUpperEstimate(thirdEstimate?.highestPrice);
                  trackEvent(GTM_VIEW_APPRAISAL_ESTIMATE_FOR_SALE_RANGE);
                } else {
                  trackEvent(GTM_VIEW_APPRAISAL_NO_ESTIMATE);
                }
              });
            }
          });
        } else {
          setZoopraisalData(data);
          if (data?.isAvailable) {
            trackEvent(GTM_VIEW_APPRAISAL_ESTIMATE);
          } else {
            trackEvent(GTM_VIEW_APPRAISAL_NO_ESTIMATE);
          }
        }

        fetchSimilarListings(data?.averagePrice);
        setIsLoading(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return (
    <div className={styles.component}>
      <div className={styles['is-loading']}>
        <LoadingSpinner />
      </div>
    </div>
  );

  return (
    <div className={buildClassName(styles.component, isExp && styles.exp)}>
      {noEstimate &&
        <div className={styles.empty}>
          <Image width={255} height={205} src={emptyImage} alt="Estimate not available" />
          <h3>Estimate not available</h3>
          <p>We appreciate your interest! However, we currently do not have enough information to provide an estimate. We recommend speaking to an agent to assist you with receiving an accurate estimate.</p>
          <Button label="Speak to An Agent" theme="primary" onClick={speakToAnAgent} />
          <Link href="/home-appraisal" onClick={() => trackEvent(GTM_CLICK_APPRAISAL_NO_ESTIMATE_BACK_TO_SEARCH)}>Back to Search</Link>
        </div>
      }
      {!noEstimate && listing &&
        <div className={styles.results}>
          <div className={styles['listing-content']}>
            <div className={styles['left-section']}>
              <div className={styles['top-bar']}>
                <h2>{is10kmEstimate ? 'Similar properties currently For Sale are estimated between: ' : 'Zoopraisal Estimate'}</h2>
                <Link href="/home-appraisal/property-details" onClick={() => trackEvent(GTM_CLICK_APPRAISAL_EDIT_PROPERTY_DETAILS)}><EditIcon /> Edit property details</Link>
              </div>
              <h1>{isExpandedEstimate ? `${formatCurrency(lowerEstimate)} - ${formatCurrency(upperEstimate)}` : formatCurrency(zoopraisalData.averagePrice!)}</h1>
              {isExpandedEstimate &&
              <>
                <div className={styles.disclaimer}>
                  <InfoIcon />
                  <div>
                    <p>{notEnoughDataDisclaimer}</p>
                  </div>
                </div>
                <div className={styles['expanded-estimate-cta']} onClick={getEstimate}>Request a free home estimate from one of our agents</div>
              </>}
              {parsedAddress
                ? <p className={styles.address}>{listing.seoStreet}</p>
                : <p className={styles.address}>{`${listing.seoStreet}, ${listing.city}${listing.city ? ',' : ''} ${listing.province ? provinceOrStateNameFromCode(listing.province.toLowerCase() as ProvinceOrStateCode) : ''}, ${listing.postalCode}`}</p>
              }
              <Link href="/home-appraisal/property-details" className={styles['stats-container']}>
                <span className={styles.stat}><HomeTypeIcon className={styles.building} />{capitalize(listing.homeType!)}</span>
                <span className={styles.stat}><BedIcon className={styles.bed} />{listing.bedrooms}</span>
                <span className={styles.stat}><BathIcon className={styles.bath} />{listing.bathrooms}</span>
                <span className={styles.stat}><SqftIcon className={styles.sqft} /><SqftText squareFootageLabel={listing.sqftLabel} render={formattedSqft => <>{formattedSqft}</>} /></span>
                <span className={styles.stat}><CarIcon className={styles.car} />{listing.parking || 0}</span>
                {!!listing.locker && <span className={styles.stat}><LockerIcon className={styles.car} /> Has Locker</span>}
              </Link>
              {!isExpandedEstimate &&
              <div className={styles.cta} onClick={getEstimate}>
                <InfoIcon />
                <div>
                  <p>Talk to our agent to learn how we got to this estimate.</p>
                  <p>By the way, it’s  Free!</p>
                </div>
              </div>
              }
              {!!listing.id && listing.id > 0
                ? <PriceHistory isVisible={!!listing.id && listing.id > 0} listing={listing} listings={nonDeletedListings} />
                : <div className={styles.spacer} />}
              {!isExp && <ValuationTips listing={listing}/>}
            </div>
            {isNotMobile &&
              <div className={styles['request-info-section']}>
                <HomeAppraisalConnectionRequest listing={listing} />
              </div>
            }
          </div>
          {!isNotMobile &&
            <div className={styles['pvr-cta-wrapper']}>
              <Button label="Get a Detail Estimate for Free" theme="primary" className={styles['pvr-cta-button']} onClick={getDetailedEstimate} />
            </div>
          }
        </div>
      }
      { similarAvailableListingState.similarListings && similarAvailableListingState.similarListings.length > 0 &&
        <SimilarListings
          heading={<span className={buildClassName(styles['similar-heading'], styles['similar-available'])} dangerouslySetInnerHTML={{ __html: dynamicAvailableListingsTitle || 'Similar <b>For Sale</b> Listings Nearby' }} />}
          isLoading={similarAvailableListingState.isLoading}
          listings={similarAvailableListingState.similarListings}
          noEstimate={noEstimate}
          showCarousel
          componentUsage={SimilarListingsComponentUsage.HomeAppraisalAvailable}
          showPriceRange={true}
        />
      }
      { similarSoldListingState.similarListings && similarSoldListingState.similarListings.length > 0 &&
        <SimilarListings
          heading={<span className={buildClassName(styles['similar-heading'], styles['similar-sold'])}>Similar <b>Sold</b> Listings Nearby</span>}
          isLoading={similarSoldListingState.isLoading}
          listings={similarSoldListingState.similarListings}
          noEstimate={noEstimate}
          showCarousel
          componentUsage={SimilarListingsComponentUsage.HomeAppraisalSold}
          showPriceRange={true}
        />
      }
    </div>
  );
}
